import { Timestamp } from 'firebase/firestore';

/**
 * Notification Type
 *
 * Different types of system notifications
 */
export type NotificationType =
  | 'delivery_arrival'
  | 'pickup_reminder'
  | 'invoice_ready'
  | 'payment_due'
  | 'payment_overdue'
  | 'service_disruption'
  | 'item_retired'
  | 'item_replacement'
  | 'issue_ticket_update'
  | 'issue_resolved'
  | 'low_inventory'
  | 'anomaly_alert';

/**
 * Notification
 *
 * User notification for real-time alerts
 */
export interface Notification {
  id: string;
  user_id: string;
  tenant_id: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  link?: string; // Optional link to related resource
  created_at: Timestamp;
}

/**
 * Create Notification Input
 */
export interface CreateNotificationInput {
  user_id: string;
  tenant_id: string;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
}

/**
 * Notification Icon Config
 */
export interface NotificationIconConfig {
  icon: string;
  color: string;
  bgColor: string;
}

/**
 * Notification display configurations
 */
export const NOTIFICATION_ICONS: Record<NotificationType, NotificationIconConfig> = {
  delivery_arrival: {
    icon: '📦',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  pickup_reminder: {
    icon: '🔔',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  invoice_ready: {
    icon: '💰',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  payment_due: {
    icon: '💳',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
  },
  payment_overdue: {
    icon: '⚠️',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
  service_disruption: {
    icon: '🚨',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
  item_retired: {
    icon: '🗑️',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
  },
  item_replacement: {
    icon: '🔄',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  issue_ticket_update: {
    icon: '🎫',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  issue_resolved: {
    icon: '✅',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  low_inventory: {
    icon: '📉',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  anomaly_alert: {
    icon: '⚠️',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
};

/**
 * Format relative time for notifications
 */
export function formatRelativeTime(timestamp: Timestamp): string {
  const now = new Date();
  const date = timestamp.toDate();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
}

/**
 * Group notifications by time period
 */
export interface GroupedNotifications {
  today: Notification[];
  yesterday: Notification[];
  thisWeek: Notification[];
  older: Notification[];
}

export function groupNotificationsByTime(notifications: Notification[]): GroupedNotifications {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  const grouped: GroupedNotifications = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: [],
  };

  notifications.forEach((notification) => {
    const date = notification.created_at.toDate();

    if (date >= today) {
      grouped.today.push(notification);
    } else if (date >= yesterday) {
      grouped.yesterday.push(notification);
    } else if (date >= weekAgo) {
      grouped.thisWeek.push(notification);
    } else {
      grouped.older.push(notification);
    }
  });

  return grouped;
}

/**
 * Notification Frequency
 *
 * How often users receive notifications
 */
export type NotificationFrequency = 'immediate' | 'daily_digest' | 'weekly_digest';

/**
 * Priority Filter
 *
 * Filter notifications by priority level
 */
export type NotificationPriorityFilter = 'all' | 'high_priority' | 'critical_only';

/**
 * Quiet Hours Configuration
 *
 * Time period during which notifications are suppressed
 */
export interface QuietHours {
  enabled: boolean;
  start_time: string; // Format: "HH:MM" (24-hour)
  end_time: string;   // Format: "HH:MM" (24-hour)
}

/**
 * Email Notification Preferences
 *
 * Email notification settings by notification type
 */
export interface EmailNotificationPreferences {
  delivery_arrival: boolean;
  pickup_reminder: boolean;
  invoice_ready: boolean;
  payment_due: boolean;
  payment_overdue: boolean;
  service_disruption: boolean;
  item_retired: boolean;
  item_replacement: boolean;
  issue_ticket_update: boolean;
  issue_resolved: boolean;
  low_inventory: boolean; // admin only
  anomaly_alert: boolean; // admin only
}

/**
 * In-App Notification Preferences
 *
 * In-app notification settings by notification type
 */
export interface InAppNotificationPreferences {
  delivery_arrival: boolean;
  pickup_reminder: boolean;
  invoice_ready: boolean;
  payment_due: boolean;
  payment_overdue: boolean;
  service_disruption: boolean;
  item_retired: boolean;
  item_replacement: boolean;
  issue_ticket_update: boolean;
  issue_resolved: boolean;
  low_inventory: boolean; // admin only
  anomaly_alert: boolean; // admin only
}

/**
 * User Notification Preferences (FR33)
 *
 * Comprehensive notification preferences for users
 */
export interface UserNotificationPreferences {
  email: EmailNotificationPreferences;
  in_app: InAppNotificationPreferences;
  frequency: NotificationFrequency;
  quiet_hours: QuietHours;
  priority_filter: NotificationPriorityFilter;
}

/**
 * Default Notification Preferences
 *
 * Default settings when user hasn't configured preferences
 */
export const DEFAULT_NOTIFICATION_PREFERENCES: UserNotificationPreferences = {
  email: {
    delivery_arrival: true,
    pickup_reminder: true,
    invoice_ready: true,
    payment_due: true,
    payment_overdue: true,
    service_disruption: true,
    item_retired: true,
    item_replacement: true,
    issue_ticket_update: true,
    issue_resolved: true,
    low_inventory: true,
    anomaly_alert: true,
  },
  in_app: {
    delivery_arrival: true,
    pickup_reminder: true,
    invoice_ready: true,
    payment_due: true,
    payment_overdue: true,
    service_disruption: true,
    item_retired: true,
    item_replacement: true,
    issue_ticket_update: true,
    issue_resolved: true,
    low_inventory: true,
    anomaly_alert: true,
  },
  frequency: 'immediate',
  quiet_hours: {
    enabled: false,
    start_time: '22:00',
    end_time: '08:00',
  },
  priority_filter: 'all',
};

/**
 * Notification Category
 *
 * Grouping of related notification types
 */
export interface NotificationCategory {
  id: string;
  label: string;
  description: string;
  notifications: Array<{
    key: keyof EmailNotificationPreferences;
    label: string;
    description: string;
    adminOnly?: boolean;
  }>;
}

/**
 * Notification Categories Configuration
 *
 * Organized notification settings by category
 */
export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    id: 'deliveries',
    label: 'Deliveries',
    description: 'Notifications about delivery arrivals and pickups',
    notifications: [
      {
        key: 'delivery_arrival',
        label: 'Delivery Arrival Alerts',
        description: 'Get notified when linens arrive at your facility',
      },
      {
        key: 'pickup_reminder',
        label: 'Pickup Reminders',
        description: 'Reminders about upcoming pickup schedules',
      },
    ],
  },
  {
    id: 'invoices',
    label: 'Invoices & Payments',
    description: 'Notifications about invoices and payment status',
    notifications: [
      {
        key: 'invoice_ready',
        label: 'New Invoice Available',
        description: 'Get notified when new invoices are ready',
      },
      {
        key: 'payment_due',
        label: 'Payment Due Reminders',
        description: 'Reminders about upcoming payment due dates',
      },
      {
        key: 'payment_overdue',
        label: 'Overdue Payment Alerts',
        description: 'Alerts for overdue payments requiring attention',
      },
    ],
  },
  {
    id: 'service',
    label: 'Service Updates',
    description: 'Notifications about service changes and disruptions',
    notifications: [
      {
        key: 'service_disruption',
        label: 'Service Disruptions',
        description: 'Alerts about schedule changes or service issues',
      },
    ],
  },
  {
    id: 'items',
    label: 'Items & Inventory',
    description: 'Notifications about item status and replacements',
    notifications: [
      {
        key: 'item_retired',
        label: 'Item Retirement Notices',
        description: 'Notifications when items are retired from service',
      },
      {
        key: 'item_replacement',
        label: 'Replacement Recommendations',
        description: 'Suggestions for replacing aging items',
      },
    ],
  },
  {
    id: 'issues',
    label: 'Issues & Support',
    description: 'Notifications about issue tickets and resolutions',
    notifications: [
      {
        key: 'issue_ticket_update',
        label: 'Ticket Updates',
        description: 'Updates on your reported issues',
      },
      {
        key: 'issue_resolved',
        label: 'Resolution Notifications',
        description: 'Notifications when issues are resolved',
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    description: 'Admin-only operational alerts and monitoring',
    notifications: [
      {
        key: 'low_inventory',
        label: 'Low Inventory Alerts',
        description: 'Alerts when inventory levels are low',
        adminOnly: true,
      },
      {
        key: 'anomaly_alert',
        label: 'Anomaly Alerts',
        description: 'Notifications about detected anomalies',
        adminOnly: true,
      },
    ],
  },
];
